import { FC, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Box, formattedCurrency, TransactionsLists, withStyles, Card } from 'vopay';
import { TransactionCard, Title, DetailsRows } from 'UI';
import { useToast } from '@/providers/Toast.provider';
import { useClientData } from '@/providers/ClientData.provider';
import useAccountTransfer from '@/hooks/useApi/useAccountTransfer';
import styles from '../Transactions.module.scss';

const cn = withStyles(styles);

type TransactionInfoProps = {
  transaction: TransactionsLists;
  transactionID: string;
  jwt: string;
};

const AccountTransferTransactionInfo: FC<TransactionInfoProps> = ({
  transaction,
  transactionID,
  jwt,
}: TransactionInfoProps) => {
  const toast = useToast();
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.AccountTransferInfo',
  });
  const { t: tGlobal } = useTranslation('translations');
  const {
    response: { JWT: mainJwt },
  } = useClientData();
  const isMainAccount = !jwt || jwt === mainJwt;
  const { accountTransferInformation, fetchAccountTransfer } = useAccountTransfer();
  const isWalletTransfer = accountTransferInformation?.Notes?.toLowerCase().includes('wallet transfer') ?? false;
  const walletTransferText = isWalletTransfer
    ? accountTransferInformation!.Notes.replace(/wallet transfer from /i, '')
    : '';
  // use lastIndexOf to handle wallet names that contain 'to' (e.g. 'today', 'Toronto')
  const lastToIndex = walletTransferText.lastIndexOf(' to ');
  const walletFrom = lastToIndex !== -1 ? walletTransferText.slice(0, lastToIndex) : walletTransferText;
  const walletTo = lastToIndex !== -1 ? walletTransferText.slice(lastToIndex + 4) : '';

  const extraInformation = isWalletTransfer ? (
    <Title level="6" emphasized={false} className={cn('extraInformation')}>
      <Trans
        ns="translations"
        i18nKey="Transaction.TransactionHistory.Details.AccountTransferInfo.transferMessage"
        values={{ walletFrom, walletTo }}
        defaultValue="{{walletFrom}} to {{walletTo}}"
        components={{
          span: <span className={cn('greyText')} />,
        }}
      />
    </Title>
  ) : null;

  useEffect(() => {
    if (!transactionID) return;

    (async () => {
      try {
        await fetchAccountTransfer(
          {
            TransactionID: transactionID,
          },
          jwt,
        );
      } catch (err) {
        console.error(err);
        toast.neutral(t('fetchingError', { defaultValue: 'Failed to fetch account transfer information.' }));
      }
    })();
  }, [transactionID]);

  const transactionDetails = [
    {
      label: t('TransactionDetails.type', { defaultValue: 'Transaction Type' }),
      value: tGlobal('Transaction.TransactionHistory.transactionTypes.Account Transfer', {
        defaultValue: 'Account Transfer',
      }),
    },
    {
      label: t('TransactionDetails.transactionTypeCode', { defaultValue: 'Transaction Type Code' }),
      value: transaction.TransactionTypeCode || '-',
    },
    {
      label: t('TransactionDetails.transactionDate', { defaultValue: 'Transaction Date' }),
      value: transaction.TransactionDateTime,
    },
    {
      label: t('TransactionDetails.lastModified', { defaultValue: 'Last Modified' }),
      value: transaction.LastModified,
    },
    {
      label: t('TransactionDetails.holdAmount', { defaultValue: 'Hold Amount' }),
      value: transaction.HoldAmount ? `${transaction.Currency} ${formattedCurrency(transaction.HoldAmount)}` : '-',
    },
    {
      label: t('TransactionDetails.clientReferenceNumber', { defaultValue: 'Client Reference Number' }),
      value: transaction.ClientReferenceNumber || '-',
    },
    {
      label: t('TransactionDetails.glCode', { defaultValue: 'GL Code' }),
      value: transaction.GLCode || '-',
    },
    {
      label: t('TransactionDetails.paymentRailID', { defaultValue: 'Payment Rail ID' }),
      value: transaction.PaymentRailID || '-',
    },
    {
      label: t('TransactionDetails.notes', { defaultValue: 'Notes' }),
      value: transaction.Notes || '-',
    },
  ];

  const errorDetails = [
    {
      label: t('ErrorInfo.failureDateTime', { defaultValue: 'Failure Date Time' }),
      value: transaction.LastModified,
    },
    {
      label: t('ErrorInfo.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${formattedCurrency(transaction.CreditAmount || '0.00')}`,
    },
    {
      label: t('ErrorInfo.failureReason', { defaultValue: 'Failure Reason' }),
      value: transaction.TransactionFailureReason || '-',
    },
  ];

  return (
    <Box flx="col" gapSize="24px">
      <TransactionCard
        receiverName={accountTransferInformation?.Receiver || ''}
        senderName={accountTransferInformation?.Sender}
        amount={parseFloat(transaction.CreditAmount) === 0 ? transaction.DebitAmount : transaction.CreditAmount || ''}
        currency={transaction.Currency || ''}
        transactionId={transaction.TransactionID || ''}
        type={'Account Transfer'}
        failureReason={transaction.TransactionFailureReason || ''}
        jwt={jwt}
        isMainAccount={isMainAccount}
        contactId={transaction.ClientAccountID}
        extraInformation={extraInformation}
      />
      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('transactionDetailsTitle', { defaultValue: 'Transaction Details' })}
        </Title>
        <DetailsRows className={cn('rows')} details={transactionDetails} />
      </Card>
      {transaction?.TransactionFailureReason && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('errorInfoTitle', { defaultValue: 'Error Info' })}
          </Title>
          <DetailsRows className={cn('rows')} details={errorDetails} />
        </Card>
      )}
    </Box>
  );
};

export default AccountTransferTransactionInfo;
